import { signal, computed } from '@preact/signals';

/**
 * Стор установки PWA.
 *
 * Android/Chrome/Samsung: ловим beforeinstallprompt, показываем нативный промпт.
 * iOS Safari: события нет — показываем инструкцию «Поделиться → На экран Домой».
 */

// Отложенный системный prompt (Android). null, если недоступен.
let deferredPrompt = null;

// Можно показать нативный системный промпт установки.
export const canInstall = signal(false);
// Приложение уже запущено как установленное (standalone).
export const isStandalone = signal(detectStandalone());
// Платформа iOS (нужна своя инструкция установки).
export const isIOS = signal(detectIOS());
// Открыт модал с iOS-инструкцией.
export const iosInstallModalOpen = signal(false);
// Тик для пересчёта видимости баннера (localStorage не реактивен).
const dismissTick = signal(0);

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 7;

/** Виден ли баннер-предложение установки. */
export const bannerVisible = computed(() => {
  dismissTick.value; // зависимость: dismiss мгновенно скрывает баннер
  if (isStandalone.value) return false;
  if (isBannerDismissed()) return false;
  // Android — когда система готова; iOS — всегда (нет системного промпта).
  return canInstall.value || isIOS.value;
});

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  const mm = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari использует нестандартный navigator.standalone.
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  return !!(mm || iosStandalone);
}

function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIPhoneIPad = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ маскируется под Mac — ловим по тач-поинтам.
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIPhoneIPad || isIPadOS;
}

function isBannerDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

/** Скрыть баннер на DISMISS_DAYS дней. */
export function dismissInstallBanner() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* приватный режим — игнор */
  }
  dismissTick.value++;
}

/**
 * Запустить установку.
 * Android: системный промпт. iOS: открыть модал-инструкцию.
 * Возвращает 'accepted' | 'dismissed' | 'ios' | 'unavailable'.
 */
export async function promptInstall() {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    canInstall.value = false;
    return outcome; // 'accepted' | 'dismissed'
  }
  if (isIOS.value) {
    iosInstallModalOpen.value = true;
    return 'ios';
  }
  return 'unavailable';
}

/** Можно ли показывать пункт «Установить приложение» в меню. */
export const canShowInstallEntry = computed(() => {
  if (isStandalone.value) return false;
  return canInstall.value || isIOS.value;
});

/** Инициализация слушателей. Вызывать как можно раньше (main.jsx). */
export function initInstall() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Не даём браузеру показать мини-инфобар — управляем сами.
    e.preventDefault();
    deferredPrompt = e;
    canInstall.value = true;
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    canInstall.value = false;
    isStandalone.value = true;
  });

  // Реакция на смену display-mode (установка/запуск как standalone).
  if (window.matchMedia) {
    const mq = window.matchMedia('(display-mode: standalone)');
    const onChange = (e) => {
      isStandalone.value = e.matches;
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }
}
